/**
 * Users API Client
 * API functions for user management (admin) and profile (self-service)
 */

import { get, post, patch, del } from '@/lib/api-client'
import type {
  ManagedUser,
  UserListResponse,
  ListUsersParams,
  CreateUserRequest,
  UpdateUserRequest,
  AdminResetPasswordRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from '@/types/user-management'

// ============================================================================
// ADMIN USER ENDPOINTS
// ============================================================================

/**
 * Fetch paginated list of users with optional filters
 */
export async function fetchUsers(params: ListUsersParams = {}): Promise<UserListResponse> {
  const queryParams = new URLSearchParams()

  if (params.page !== undefined) queryParams.set('page', String(params.page))
  if (params.pageSize !== undefined) queryParams.set('pageSize', String(params.pageSize))
  if (params.search) queryParams.set('search', params.search)
  if (params.role) queryParams.set('role', params.role)
  if (params.status) queryParams.set('status', params.status)

  const queryString = queryParams.toString()
  const url = `/users${queryString ? `?${queryString}` : ''}`

  return get<UserListResponse>(url)
}

/**
 * Fetch a single user by ID
 */
export async function fetchUser(id: string): Promise<ManagedUser> {
  return get<ManagedUser>(`/users/${id}`)
}

/**
 * Create a new platform user
 */
export async function createUser(data: CreateUserRequest): Promise<ManagedUser> {
  return post<ManagedUser>('/users', data)
}

/**
 * Update an existing user (admin)
 */
export async function updateUser(id: string, data: UpdateUserRequest): Promise<ManagedUser> {
  return patch<ManagedUser>(`/users/${id}`, data)
}

/**
 * Delete a user permanently
 */
export async function deleteUser(id: string): Promise<void> {
  await del(`/users/${id}`)
}

/**
 * Admin: reset a user's password
 */
export async function adminResetPassword(
  id: string,
  data: AdminResetPasswordRequest
): Promise<void> {
  await post(`/users/${id}/reset-password`, data)
}

/**
 * Deactivate a user account
 */
export async function deactivateUser(id: string): Promise<ManagedUser> {
  return post<ManagedUser>(`/users/${id}/deactivate`, {})
}

/**
 * Reactivate a user account
 */
export async function reactivateUser(id: string): Promise<ManagedUser> {
  return post<ManagedUser>(`/users/${id}/reactivate`, {})
}

// ============================================================================
// SELF-SERVICE PROFILE ENDPOINTS
// ============================================================================

/**
 * Get the current user's profile
 */
export async function fetchProfile(): Promise<ManagedUser> {
  return get<ManagedUser>('/profile')
}

/**
 * Update the current user's profile
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<ManagedUser> {
  return patch<ManagedUser>('/profile', data)
}

/**
 * Change the current user's password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await post('/profile/change-password', data)
}
